/**
 * May this plan be assigned, and if not, what does the trainer read instead?
 *
 * The builder's top bar has carried a disabled Assign button with a worded reason since PR-B1. This
 * hook is what turns that reason into an answer about THIS plan rather than about the feature not
 * existing — and it is a separate entry point from the dialog on purpose: the button lives in the
 * top bar, which the v3 canon lays out, and a feature exporting its own button would either
 * duplicate that bar's styling or dictate it.
 *
 * ── A stored plan is judged by the query, an unsaved one by the store ────────────────────────
 * `assignability` judges the STORED tree, because that is the tree the backend forks. The draft
 * contributes exactly one fact — whether it has diverged from it — and that fact closes the door.
 *
 * An UNSAVED plan built for a client (23 September 2026) has no stored tree: it is assigned with its
 * own tree in the request, so `unsavedAssignability` judges that — through the store's
 * `useUnsavedAssignBlockingPhaseName`, the same predicate the serialiser refuses on, asked only while
 * a client is set so a stored plan never walks its tree for it. A plan with no id and no
 * client is still refused `unsaved` by the stored path: from the library it is saved first.
 *
 * The observer this mounts costs no request: `planTemplateFullQueryOptions` is `staleTime: Infinity`
 * and the builder screen has already loaded the same key, so this hook reads the cache the moment
 * it mounts and never refetches.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  planTemplateFullQueryOptions,
  useIsPlanDraftDirty,
  usePlanRecipient,
  useUnsavedAssignBlockingPhaseName,
} from '~/entities/plan-draft';

import { assignability, unsavedAssignability } from '../model/assignTarget';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

export interface AssignGate {
  readonly canAssign: boolean;
  /** Why not, already worded. `null` exactly when `canAssign` is true. */
  readonly disabledReason: string | null;
}

export function useAssignGate(planId: number | null): AssignGate {
  const { t } = useTranslation();
  const contentLang = useContentLanguage();

  const query = useQuery({
    ...planTemplateFullQueryOptions(planId ?? 0, contentLang),
    enabled: planId !== null,
  });
  const dirty = useIsPlanDraftDirty();
  const recipient = usePlanRecipient();
  const emptyPhase = useUnsavedAssignBlockingPhaseName();

  // `planId` goes in as well as gating the query: a null one is not a read that has not landed, it
  // is a plan with no stored row at all, and the two owe the trainer different sentences.
  const { canAssign, blocked, phase } =
    planId === null && recipient !== null
      ? unsavedAssignability(emptyPhase)
      : assignability(query.data, dirty, planId);

  return {
    canAssign,
    disabledReason: blocked === null ? null : t(`web:assign.blocked.${blocked}`, { phase: phase ?? '' }),
  };
}
